import { useCallback } from 'react';

import { usePrivacy } from '@/contexts/PrivacyContext';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { formatCurrency } from '@/lib/format';

const NBSP = '\u00A0'; // pt-BR currency separator between "R$" and the number

export interface CompactCurrencyOptions {
  /**
   * Thousands spelling: `'mil'` → "R$ 3,02 mil" (headlines/prose), `'k'` →
   * "R$ 7,2k" (chips/legends). Millions always read "mi" and values under
   * R$ 1.000 print in full ("R$ 718") regardless of style.
   */
  style?: 'mil' | 'k';
  /** Fraction digits for the scaled number. Defaults 0–1 → "7,2" but "7". */
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  /**
   * Absolute value from which the thousands form drops its fraction digits
   * entirely (e.g. `100_000` → "R$ 123k", never "R$ 123,5k"). The millions
   * form keeps the regular fraction digits. Omit to never drop.
   */
  integerFrom?: number;
}

/**
 * The prototype's compact pt-BR money spellings shared by the home widgets
 * (neither exists in `@/lib/format`'s `compactBrl`, whose 10k cutoff / "M"
 * suffix / full-precision fallback are a different, chart-axis dialect).
 * NBSP separators mirror Intl's pt-BR currency output so amounts never wrap.
 *
 * NOT privacy-aware — components must go through `useCompactCurrency`; this
 * pure form exists for the hook and for tests.
 */
export function formatCompactCurrency(
  value: number,
  options: CompactCurrencyOptions = {},
): string {
  const {
    style = 'mil',
    minimumFractionDigits = 0,
    maximumFractionDigits = 1,
    integerFrom,
  } = options;
  const abs = Math.abs(value);
  if (abs < 1_000) return formatCurrency(value, { noCents: true });

  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000) {
    const scaled = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(abs / 1_000_000);
    return `${sign}R$${NBSP}${scaled}${NBSP}mi`;
  }

  const whole = integerFrom !== undefined && abs >= integerFrom;
  const scaled = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: whole ? 0 : minimumFractionDigits,
    maximumFractionDigits: whole ? 0 : maximumFractionDigits,
  }).format(abs / 1_000);
  return style === 'k' ? `${sign}R$${NBSP}${scaled}k` : `${sign}R$${NBSP}${scaled}${NBSP}mil`;
}

/**
 * Privacy-aware compact BRL formatter. Privacy comes first and is read from
 * `PrivacyContext` (not sniffed from formatted output): while values are
 * hidden, the masked full form ("R$ •••••") is returned untouched — a hidden
 * value is never compacted, so its magnitude cannot leak through "mil"/"k".
 */
export function useCompactCurrency(
  options: CompactCurrencyOptions = {},
): (value: number) => string {
  const {
    style = 'mil',
    minimumFractionDigits = 0,
    maximumFractionDigits = 1,
    integerFrom,
  } = options;
  const { hideValues } = usePrivacy();
  const formatFull = useFormatCurrency();

  return useCallback(
    (value: number) => {
      if (hideValues) return formatFull(value, { noCents: true });
      return formatCompactCurrency(value, {
        style,
        minimumFractionDigits,
        maximumFractionDigits,
        integerFrom,
      });
    },
    [hideValues, formatFull, style, minimumFractionDigits, maximumFractionDigits, integerFrom],
  );
}
